import { useState } from 'react';
import { useAppDispatch } from '@/stores/hooks';
import { ConflictResolution, ConflictResolutionStrategy, ConflictDetail } from '@/stores/thunks/resolve-conflict-thunk';
import { saveVaultThunk } from '@/stores/thunks/save-vault-thunk';
import { uploadVaultThunk } from '@/stores/thunks/upload-vault-thunk';
import { setCurrentVault } from '@/stores/vault-slice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { PrimaryButton } from './ui/primary-button';
import { SecondaryButton } from './ui/secondary-button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ChevronRightIcon } from './icons/chevron-right-icon';
import { ChevronDownIcon } from './icons/chevron-down-icon';
import { AlertIcon } from './icons/alert-icon';
import { Badge } from './ui/badge';

interface ConflictResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflictResolution: ConflictResolution | null;
}

export function ConflictResolutionDialog({
  open,
  onOpenChange,
  conflictResolution,
}: ConflictResolutionDialogProps) {
  const dispatch = useAppDispatch();
  const [expandedConflicts, setExpandedConflicts] = useState<Set<string>>(new Set());
  const [selectedStrategy, setSelectedStrategy] = useState<ConflictResolutionStrategy>(
    ConflictResolutionStrategy.UseLocal
  );
  const [resolving, setResolving] = useState(false);

  if (!conflictResolution) return null;

  const toggleConflict = (conflictKey: string) => {
    setExpandedConflicts((prev) => {
      const next = new Set(prev);
      if (next.has(conflictKey)) {
        next.delete(conflictKey);
      } else {
        next.add(conflictKey);
      }
      return next;
    });
  };

  const getConflictKey = (conflict: ConflictDetail) =>
    `${conflict.entity}-${conflict.entityId}`;

  const handleResolve = async (strategy: ConflictResolutionStrategy) => {
    setResolving(true);
    try {
      let vaultToSave;
      let forceUpload = false;

      switch (strategy) {
        case ConflictResolutionStrategy.UseLocal:
          vaultToSave = {
            ...conflictResolution.localVault,
            syncState: {
              ...conflictResolution.localVault.syncState,
              serverLastModified: conflictResolution.serverVault.syncState.serverLastModified,
            },
          };
          // Force upload to overwrite server version without conditional headers
          forceUpload = true;
          break;

        case ConflictResolutionStrategy.UseServer:
          vaultToSave = conflictResolution.serverVault;
          break;

        case ConflictResolutionStrategy.Merge:
          vaultToSave = conflictResolution.mergedVault || conflictResolution.localVault;
          // Force upload for merged version as well
          forceUpload = true;
          break;

        default:
          vaultToSave = conflictResolution.localVault;
      }

      // Update local state
      dispatch(setCurrentVault(vaultToSave));

      // Save to localStorage
      await dispatch(saveVaultThunk({ vault: vaultToSave }));

      // Upload to server (with force flag if keeping local changes)
      await dispatch(uploadVaultThunk({ 
        vaultId: vaultToSave.info.id, 
        forceUpload 
      }));

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
      // Error handling could be improved here
    } finally {
      setResolving(false);
    }
  };

  const getEntityDisplayName = (entityType: string) => {
    const names: Record<string, string> = {
      people: 'People',
      vehicles: 'Vehicles',
      properties: 'Properties',
      liabilities: 'Liabilities',
      items: 'Items',
      protection: 'Protection',
      savings: 'Savings',
      incomes: 'Incomes',
      expenditures: 'Expenditures',
      documents: 'Documents',
    };
    // eslint-disable-next-line security/detect-object-injection
    return names[entityType] || entityType;
  };

  const renderConflictValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">Deleted</span>;
    }
    
    // Show a simplified view of the entity
    const displayProps = ['name', 'registration', 'provider', 'policy', 'address'];
    const displayText = displayProps
      // eslint-disable-next-line security/detect-object-injection
      .map((prop) => (value as Record<string, unknown>)[prop])
      .filter(Boolean)
      .join(' - ');

    return (
      <div className="space-y-1">
        <div className="font-medium">{displayText || 'Modified'}</div>
        <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={ e => e.preventDefault() }>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertIcon className="h-6 w-6 text-yellow-500" />
            <DialogTitle>Sync Conflict Detected</DialogTitle>
          </div>
          <DialogDescription>
            The vault has been modified on another device. Choose how to resolve the conflict.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conflict Summary</CardTitle>
              <CardDescription>
                {conflictResolution.conflicts?.length || 0} conflicting changes detected
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium text-muted-foreground">Local Version</div>
                  <div>
                    Last modified:{' '}
                    {new Date(conflictResolution.localVault.syncState.lastChangeDate).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Server Version</div>
                  <div>
                    Last synced:{' '}
                    {new Date(conflictResolution.serverVault.syncState.lastSyncDate).toLocaleString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resolution Strategy Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resolution Strategy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant={selectedStrategy === ConflictResolutionStrategy.UseLocal ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setSelectedStrategy(ConflictResolutionStrategy.UseLocal)}
              >
                <div className="text-left">
                  <div className="font-semibold">Keep My Changes</div>
                  <div className="text-xs text-muted-foreground">
                    Overwrite server version with your local changes
                  </div>
                </div>
              </Button>

              <Button
                variant={selectedStrategy === ConflictResolutionStrategy.UseServer ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setSelectedStrategy(ConflictResolutionStrategy.UseServer)}
              >
                <div className="text-left">
                  <div className="font-semibold">Use Server Version</div>
                  <div className="text-xs text-muted-foreground">
                    Discard your local changes and use the server version
                  </div>
                </div>
              </Button>

              {conflictResolution.mergedVault && (
                <Button
                  variant={selectedStrategy === ConflictResolutionStrategy.Merge ? 'default' : 'outline'}
                  className="w-full justify-start"
                  onClick={() => setSelectedStrategy(ConflictResolutionStrategy.Merge)}
                >
                  <div className="text-left">
                    <div className="font-semibold">Smart Merge</div>
                    <div className="text-xs text-muted-foreground">
                      Automatically merge non-conflicting changes
                    </div>
                  </div>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Detailed Conflicts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conflict Details</CardTitle>
              <CardDescription>Click to expand and review each conflict</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {conflictResolution.conflicts?.map((conflict) => {
                const key = getConflictKey(conflict);
                const isExpanded = expandedConflicts.has(key);

                return (
                  <div key={key} className="border rounded-lg">
                    <button
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                      onClick={() => toggleConflict(key)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                        <div className="text-left">
                          <div className="font-medium">
                            {getEntityDisplayName(conflict.entity)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            ID: {conflict.entityId.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {conflict.canAutoResolve && (
                          <Badge variant="secondary">Auto-resolvable</Badge>
                        )}
                        {conflict.suggestedResolution && (
                          <Badge variant="outline">{conflict.suggestedResolution}</Badge>
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4 border-t">
                        <div className="grid grid-cols-2 gap-4 pt-4">
                          <div>
                            <div className="text-sm font-medium mb-2">Your Version</div>
                            {renderConflictValue(conflict.localValue)}
                          </div>
                          <div>
                            <div className="text-sm font-medium mb-2">Server Version</div>
                            {renderConflictValue(conflict.serverValue)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <SecondaryButton onClick={() => onOpenChange(false)} disabled={resolving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={() => handleResolve(selectedStrategy)} disabled={resolving}>
            {resolving ? 'Resolving...' : 'Resolve Conflict'}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
